import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import StatusBadge from '@/components/base/StatusBadge';
import { adminApi } from '@/lib/adminApi';
import type { AdminOverview } from '@/lib/adminApi';
import { formatDate, formatMoney } from '@/lib/format';
import { AdminCard, EmptyBlock, ErrorBlock, LoadingBlock, StatCard, TableCell, TableHeaderCell } from './ui';

export default function OverviewTab({ token, onNavigate }: { token: string; onNavigate: (tab: string) => void }) {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<AdminOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await adminApi.overview(token);
      if (!res.ok) {
        setFailed(true);
        return;
      }
      setData(res as unknown as AdminOverview);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <LoadingBlock />;
  if (failed || !data) return <ErrorBlock onRetry={load} />;

  const { stats } = data;
  const statusRows = [
    { key: 'pending', value: stats.pendingTransfers, tone: 'bg-accent-500' },
    { key: 'verified', value: stats.verifiedTransfers, tone: 'bg-secondary-500' },
    { key: 'executed', value: stats.executedTransfers, tone: 'bg-primary-600' },
    { key: 'cancelled', value: stats.cancelledTransfers, tone: 'bg-foreground-400' },
  ];
  const totalStatus = statusRows.reduce((sum, row) => sum + row.value, 0) || 1;

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          label={t('admin.overview.clients')}
          value={String(stats.clients)}
          hint={t('admin.overview.activeHint', { count: stats.activeClients })}
          icon="ri-group-line"
          tone="primary"
        />
        <StatCard
          label={t('admin.overview.transfers')}
          value={String(stats.transfers)}
          hint={t('admin.overview.allTime')}
          icon="ri-exchange-dollar-line"
          tone="default"
        />
        <StatCard
          label={t('admin.overview.transfersPending')}
          value={String(stats.pendingTransfers)}
          hint={t('admin.overview.awaiting')}
          icon="ri-time-line"
          tone="accent"
        />
        <StatCard
          label={t('admin.overview.transfersExecuted')}
          value={String(stats.executedTransfers)}
          hint={t('admin.overview.completed')}
          icon="ri-checkbox-circle-line"
          tone="default"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <AdminCard title={t('admin.overview.balances')} subtitle={t('admin.overview.byCurrency')}>
          {data.balances.length === 0 ? (
            <EmptyBlock label={t('admin.common.none')} icon="ri-wallet-3-line" />
          ) : (
            <ul className="flex flex-col divide-y divide-background-200">
              {data.balances.map((entry) => (
                <li key={entry.currency} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary-50 text-primary-700 text-[11px] font-bold font-label shrink-0">
                      {entry.currency}
                    </span>
                    <span className="text-[12.5px] text-foreground-600">
                      {t('admin.overview.accounts', { count: entry.clients })}
                    </span>
                  </div>
                  <span className="text-[14px] font-semibold text-foreground-950 tabular whitespace-nowrap">
                    {formatMoney(entry.total, entry.currency, i18n.language)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </AdminCard>

        <AdminCard title={t('admin.overview.statuses')} subtitle={t('admin.overview.statusesHint')}>
          <ul className="flex flex-col gap-3">
            {statusRows.map((row) => {
              const ratio = Math.round((row.value / totalStatus) * 100);
              return (
                <li key={row.key}>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <StatusBadge status={row.key} />
                    <span className="text-[12px] font-semibold text-foreground-700 tabular">
                      {row.value} · {ratio}%
                    </span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-background-200 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${row.tone}`}
                      style={{ width: `${Math.max(ratio, row.value > 0 ? 3 : 0)}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
          {stats.disabledLinks > 0 ? (
            <p className="mt-3 flex items-center gap-1.5 rounded-lg border border-accent-200 bg-accent-50 px-2.5 py-1.5 text-[11.5px] font-semibold text-accent-900 font-label">
              <span className="flex items-center justify-center w-3.5 h-3.5 shrink-0">
                <i className="ri-link-unlink text-[13px]" aria-hidden="true" />
              </span>
              {t('admin.overview.disabledLinks', { count: stats.disabledLinks })}
            </p>
          ) : null}
        </AdminCard>
      </div>

      <AdminCard
        title={t('admin.overview.recentTransfers')}
        actions={
          <button
            type="button"
            onClick={() => onNavigate('transfers')}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-primary-700 hover:text-primary-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('admin.overview.seeAll')}
            <span className="flex items-center justify-center w-3.5 h-3.5">
              <i className="ri-arrow-right-line text-[13px]" aria-hidden="true" />
            </span>
          </button>
        }
        padded={false}
      >
        {data.recentTransfers.length === 0 ? (
          <EmptyBlock label={t('admin.transfers.empty')} icon="ri-exchange-dollar-line" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead className="bg-background-100">
                <tr>
                  <TableHeaderCell>{t('admin.transfers.ref')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.transfers.beneficiary')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.transfers.bank')}</TableHeaderCell>
                  <TableHeaderCell align="right">{t('admin.transfers.amount')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.transfers.status')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.transfers.linkColumn')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.transfers.date')}</TableHeaderCell>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {data.recentTransfers.map((row) => (
                  <tr key={row.id} className="hover:bg-background-100/60 transition-colors">
                    <TableCell>
                      <span className="font-semibold text-foreground-950 font-label">{row.reference}</span>
                    </TableCell>
                    <TableCell>{row.beneficiaryName}</TableCell>
                    <TableCell>{row.bankName}</TableCell>
                    <TableCell align="right">{formatMoney(row.amount, row.currency, i18n.language)}</TableCell>
                    <TableCell>
                      <StatusBadge status={row.status} />
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold font-label ${
                          row.linkActive
                            ? 'bg-primary-50 text-primary-800 border-primary-200'
                            : 'bg-background-200 text-foreground-600 border-background-300'
                        }`}
                      >
                        <span className="flex items-center justify-center w-3 h-3">
                          <i
                            className={row.linkActive ? 'ri-link text-[11px]' : 'ri-link-unlink text-[11px]'}
                            aria-hidden="true"
                          />
                        </span>
                        {row.linkActive ? t('admin.transfers.linkActive') : t('admin.transfers.linkDisabled')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-foreground-500">
                        {formatDate(row.createdAt, i18n.language, true)}
                      </span>
                    </TableCell>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminCard>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <AdminCard title={t('admin.overview.recent')} padded={false}>
          {data.recentTransactions.length === 0 ? (
            <EmptyBlock label={t('admin.clients.detail.noTx')} icon="ri-exchange-line" />
          ) : (
            <ul className="divide-y divide-background-200">
              {data.recentTransactions.map((row) => (
                <li key={row.id} className="flex items-start justify-between gap-3 px-5 py-3">
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-semibold text-foreground-900 truncate">{row.label}</p>
                    <p className="mt-0.5 text-[11.5px] text-foreground-500">
                      {row.clientName ?? '—'} · {formatDate(row.createdAt, i18n.language, true)}
                    </p>
                  </div>
                  <span
                    className={`text-[12.5px] font-semibold tabular whitespace-nowrap ${
                      row.direction === 'credit' ? 'text-primary-700' : 'text-foreground-800'
                    }`}
                  >
                    {row.direction === 'credit' ? '+' : '−'}
                    {formatMoney(row.amount, row.currency, i18n.language)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </AdminCard>

        <div className="flex flex-col gap-5">
          <AdminCard title={t('admin.overview.alerts')}>
            {data.securityAlerts.length === 0 ? (
              <EmptyBlock label={t('admin.overview.noAlerts')} icon="ri-shield-check-line" />
            ) : (
              <ul className="flex flex-col gap-2.5">
                {data.securityAlerts.map((row) => (
                  <li
                    key={row.id}
                    className="flex items-start gap-2.5 rounded-xl border border-accent-200 bg-accent-50 px-3 py-2.5"
                  >
                    <span className="flex items-center justify-center w-4 h-4 mt-0.5 shrink-0 text-accent-600">
                      <i className="ri-alert-line text-sm" aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[12px] font-semibold text-accent-950 font-label">{row.action}</p>
                      <p className="mt-0.5 text-[11.5px] text-accent-900/75">
                        {row.ip ?? '—'} · {formatDate(row.createdAt, i18n.language, true)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </AdminCard>

          <AdminCard title={t('admin.overview.activity')} padded={false}>
            {data.recentLogs.length === 0 ? (
              <EmptyBlock label={t('admin.logs.empty')} icon="ri-history-line" />
            ) : (
              <ul className="divide-y divide-background-200">
                {data.recentLogs.map((row) => (
                  <li key={row.id} className="flex items-start justify-between gap-3 px-5 py-2.5">
                    <div className="min-w-0">
                      <p className="text-[12px] font-semibold text-foreground-900 font-label">{row.action}</p>
                      <p className="mt-0.5 text-[11px] text-foreground-500 truncate">
                        {row.actorLabel ?? row.actorType} {row.target ? `· ${row.target}` : ''}
                      </p>
                    </div>
                    <span className="text-[11px] text-foreground-400 whitespace-nowrap tabular">
                      {formatDate(row.createdAt, i18n.language, true)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </AdminCard>
        </div>
      </div>
    </div>
  );
}